import { useRef, useCallback, useEffect } from 'react';

import {
  usePaypalExecuteMutation,
  PaypalExecuteRequest,
  PaypalExecuteResponse,
} from '@/api/payments/paypalExecute';
import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';
import { useClient } from '@/services/client/useClient';

const buildExecutePayload = (
  client: string,
  transactionId: string,
  accountIdentifier: string,
  paymentId: string,
  payerId: string,
): PaypalExecuteRequest => ({
  client,
  transactionId,
  account_identifier: accountIdentifier,
  paymentId,
  payerId,
});

export function usePaypalExecute() {
  const onSuccess = useRef<((data: PaypalExecuteResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  const transactionIdFromState = useAppSelector(
    state => state.transaction.transactionId
  );

  const transactionIdRef = useRef(transactionIdFromState);
  useEffect(() => {
    transactionIdRef.current = transactionIdFromState;
  }, [transactionIdFromState]);

  const [paypalExecuteTrigger] = usePaypalExecuteMutation();

  const { getProviderPaypalAccountIdentifier } = usePaymentsConfig();
  const paypalAccountIdentifier = getProviderPaypalAccountIdentifier();
  const { getClientId } = useClient();

  // Trigger payment-service execute
  // API Reference: https://developer.paypal.com/sdk/js/reference/#link-onapprove
  const triggerExecuteApi = useCallback(async (
    paymentID: string | null | undefined,
    payerID: string | null | undefined,
    callbacks?: {
      onSuccess?: (data: PaypalExecuteResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    const currentTransactionId = transactionIdRef.current;

    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    if (!paypalAccountIdentifier || !paymentID || !payerID || !currentTransactionId) {
      const errorMsg = 'Error accessing Paypal params or missing transactionId';
      logger.error(errorMsg, {
        paypalAccountIdentifier, paymentID, payerID, transactionId: currentTransactionId
      });
      if (onError.current) {
        onError.current(new Error(errorMsg));
      }
      throw new Error(errorMsg);
    }

    let result;
    try {
      const payload = buildExecutePayload(
        getClientId(),
        currentTransactionId,
        paypalAccountIdentifier,
        paymentID,
        payerID,
      );
      result = await paypalExecuteTrigger(payload);
    } catch (err) {
      logger.error('Error executing Paypal transaction');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
      throw new Error('Error executing Paypal transaction');
    }

    // Handle payment-service execute errors
    if (!!result?.error || result?.data?.message !== 'success') {
      logger.error('Error executing Paypal transaction');
      logger.error(result?.error);
      if (onError.current) {
        onError.current(result?.error ?? new Error('API returned "unsuccessful" message'));
      }
      throw new Error('Error executing Paypal transaction');
    }

    // Handle payment-service execute complete
    if (onSuccess.current) {
      onSuccess.current(result.data);
    }
  }, [getClientId, paypalAccountIdentifier, paypalExecuteTrigger]);

  return {
    triggerExecuteApi,
  };
}
